import { transformEnumToLabelValueOption } from '@fruitful/fruitils';
import { useAtomValue } from 'jotai';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
    type DataTableRowEditEvent,
} from 'primereact/datatable';
import React, { useState } from 'react';
import { H6Regular, XStack, YStack } from 'smoothie';

import { DropdownEditor } from '../../common/components/GridCellEditors';
import { formatToSimpleDateTimeString } from '../../common/utils/formatting';
import { adminUserAtom, allGuidesAtom } from '../../entities/entityAtoms';
import { type AdminUser, AdminUserRole } from '../../entities/entityTypes';
import { GuideSelectionDropdown } from '../../guides/components/GuideSelectionDropdown';

interface AdminUserTableProps {
    adminUsers: AdminUser[];
    onRowEditComplete: (e: DataTableRowEditCompleteEvent) => void;
}

export const AdminUserTable = ({
    adminUsers,
    onRowEditComplete,
}: AdminUserTableProps) => {
    const adminUser = useAtomValue(adminUserAtom);
    const canEditAdminUsers = adminUser.authorization.adminUsers.canEditAll;
    const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});

    const allGuides = useAtomValue(allGuidesAtom);

    const onRowEditChange = (e: DataTableRowEditEvent) => {
        setEditingRows(e.data);
    };

    return (
        <YStack gap={16} maxWidth="100%">
            <XStack justifyContent="space-between" alignItems="center">
                <H6Regular>Admin Users</H6Regular>
            </XStack>

            <DataTable
                value={adminUsers}
                size="small"
                showGridlines
                stripedRows
                scrollable
                scrollHeight="flex"
                emptyMessage="No admin users found"
                editMode="row"
                dataKey="id"
                onRowEditChange={onRowEditChange}
                onRowEditComplete={onRowEditComplete}
                editingRows={editingRows}
            >
                <Column
                    field="id"
                    header="ID"
                    sortable
                    style={{ minWidth: '100px' }}
                />
                <Column
                    field="email"
                    header="Email"
                    sortable
                    style={{ minWidth: '200px' }}
                />
                <Column
                    field="role"
                    header="Role"
                    sortable
                    style={{ minWidth: '120px' }}
                    body={({ role }: { role: AdminUserRole }) =>
                        role.replaceAll('_', ' ')
                    }
                    editor={(options) => (
                        <DropdownEditor
                            options={options}
                            dropdownProps={{
                                options:
                                    transformEnumToLabelValueOption(
                                        AdminUserRole,
                                    ),
                            }}
                        />
                    )}
                />
                <Column
                    field="stytchMemberId"
                    header="Stych Member"
                    style={{ minWidth: '150px' }}
                />
                <Column
                    field="guideId"
                    header="Guide"
                    body={({ guideId }: AdminUser) => {
                        return (
                            allGuides.find((guide) => guide.id === guideId)
                                ?.fullName ?? '(Unknown)'
                        );
                    }}
                    editor={(options) =>
                        GuideSelectionDropdown({ options, guides: allGuides })
                    }
                />
                <Column
                    field="memberId"
                    header="Member"
                    style={{ minWidth: '150px' }}
                />
                <Column
                    field="createdAt"
                    header="Created"
                    sortable
                    style={{ minWidth: '200px' }}
                    body={({ createdAt }: AdminUser) => {
                        return formatToSimpleDateTimeString(createdAt);
                    }}
                />
                <Column
                    field="updatedAt"
                    header="Updated"
                    sortable
                    style={{ minWidth: '200px' }}
                    body={({ updatedAt }: AdminUser) => {
                        return formatToSimpleDateTimeString(updatedAt);
                    }}
                />
                {canEditAdminUsers && (
                    <Column rowEditor header="Edit" style={{ width: '7rem' }} />
                )}
            </DataTable>
        </YStack>
    );
};
